/**
 * Offline mode: setting the tablet up for its robot, and freeing it again. One app build serves
 * every robot, so a new tablet starts here: a service code, then the hospital, then one of its
 * robots that has no tablet yet.
 */
import type { SetupHospitalDto, SetupRobotDto } from '@evesix/contracts';
import { useState, type FormEvent } from 'react';
import { useSyncStatus, useTabletSetup } from '../app/context';
import { Button } from '../components/Button';
import { cx } from '../components/cx';
import { PIN_LENGTH } from '../domain/catalog';
import type { ResetResult, SetupFinish, SetupStart, SyncedPack } from '../services/tablet-setup';
import common from './common.module.css';
import styles from './SetupScreen.module.css';

type Step =
  | { readonly kind: 'code' }
  | {
      readonly kind: 'choose';
      readonly token: string;
      readonly operatorName: string;
      readonly hospitals: readonly SetupHospitalDto[];
    }
  | { readonly kind: 'done'; readonly synced: SyncedPack | null };

type Problem = Exclude<SetupStart, { ok: true }> | Exclude<SetupFinish, { ok: true }>;

function problemText(problem: Problem | Exclude<ResetResult, { ok: true }>, pending = 0): string {
  switch (problem.reason) {
    case 'invalid':
      return 'That is not an Evesix service code.';
    case 'locked':
      return `Too many wrong codes — setup is locked for ${Math.ceil(problem.retryAfterSeconds / 60)} min.`;
    case 'no_data':
      return 'Cannot reach the server. This needs mobile data: check the SIM, then try again.';
    case 'taken':
      return 'Another tablet took that robot a moment ago. Enter the code again and choose another.';
    case 'expired':
      return 'Setup took too long. Enter the service code again.';
    case 'not_found':
      return 'That robot is no longer in service. Enter the code again and choose another.';
    case 'waiting':
      return `${pending} record${pending === 1 ? '' : 's'} have not reached the server yet. Give the tablet data and wait for "Synced" first.`;
    case 'cycle_running':
      return 'A cycle is running. Wait until it has finished.';
  }
}

/** "EVE20051 · serial SN-51 · Intensive Care only": which robot, and which wards it is kept to. */
function robotLabel(robot: SetupRobotDto): string {
  return [
    robot.id,
    robot.serial && robot.serial !== robot.id ? `serial ${robot.serial}` : null,
    robot.wards.length ? `${robot.wards.join(', ')} only` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function CodeField({
  value,
  onChange,
  autoFocus = false,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly autoFocus?: boolean;
}) {
  return (
    <div className={styles.field}>
      <label className={common.fieldLabel} htmlFor="setup-code">
        EVESIX SERVICE CODE
      </label>
      <input
        id="setup-code"
        className={cx(styles.input, styles.code)}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={PIN_LENGTH}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
      />
    </div>
  );
}

/**
 * A tablet that has never been set up, or was reset. `onHold` keeps it on screen once the tablet
 * is set up, until the installer has seen what was saved.
 */
export function SetupScreen({ onHold }: { readonly onHold: (held: boolean) => void }) {
  const setup = useTabletSetup();
  const [step, setStep] = useState<Step>({ kind: 'code' });
  const [code, setCode] = useState('');
  const [hospitalId, setHospitalId] = useState('');
  const [robotId, setRobotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!setup) return null;

  const hospital =
    step.kind === 'choose' ? step.hospitals.find((h) => h.id === hospitalId) : undefined;

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const started = await setup.start(code);
    setBusy(false);
    setCode('');
    if (!started.ok) {
      setError(problemText(started));
      return;
    }
    setHospitalId('');
    setRobotId('');
    setStep({ kind: 'choose', ...started });
  };

  const submitRobot = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'choose' || !robotId) return;
    setBusy(true);
    setError(null);
    onHold(true);
    const finished = await setup.finish(step.token, robotId);
    setBusy(false);
    if (finished.ok) {
      setStep({ kind: 'done', synced: finished.synced });
      return;
    }
    onHold(false);
    setError(problemText(finished));
    if (finished.reason !== 'no_data') setStep({ kind: 'code' });
  };

  return (
    <section className={cx(common.screen, common.fadeUp, styles.screen)} aria-label="Tablet setup">
      <div className={cx(common.card, styles.card)}>
        <div className={common.kicker}>TABLET SETUP</div>
        <h2 className={styles.heading}>Set Up This Tablet</h2>

        {step.kind === 'done' ? (
          <div>
            {step.synced ? (
              <>
                <p className={styles.note}>
                  Set up for <b>{step.synced.robotId}</b> at <b>{step.synced.hospitalName}</b>, and
                  saved on this tablet for working with no data:
                </p>
                <dl className={styles.rows}>
                  <div>
                    <dt>WARDS</dt>
                    <dd>{step.synced.wards}</dd>
                  </div>
                  <div>
                    <dt>BEDS</dt>
                    <dd>{step.synced.beds}</dd>
                  </div>
                  <div>
                    <dt>OPERATORS</dt>
                    <dd>{step.synced.operators}</dd>
                  </div>
                </dl>
              </>
            ) : (
              <p className={styles.note}>
                The tablet is set up, but the data dropped before the hospital's wards, beds and
                operators arrived. They download by themselves as soon as there is data.
              </p>
            )}
            <div className={styles.actions}>
              <Button variant="primary" size="lg" onClick={() => onHold(false)}>
                START
              </Button>
            </div>
          </div>
        ) : step.kind === 'code' ? (
          <form onSubmit={(e) => void submitCode(e)}>
            <p className={styles.note}>
              Choose the robot this tablet will drive. Only an Evesix service account can set up a
              tablet, and it needs mobile data.
            </p>
            <CodeField value={code} onChange={setCode} autoFocus />
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={busy || code.length !== PIN_LENGTH}
              >
                {busy ? 'CHECKING…' : 'CONTINUE'}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={(e) => void submitRobot(e)}>
            <p className={styles.note}>Signed in as {step.operatorName}.</p>
            <div className={styles.field}>
              <label className={common.fieldLabel} htmlFor="setup-hospital">
                HOSPITAL
              </label>
              <select
                id="setup-hospital"
                className={styles.input}
                value={hospitalId}
                onChange={(e) => {
                  setHospitalId(e.target.value);
                  setRobotId('');
                }}
              >
                <option value="" disabled>
                  {step.hospitals.length ? 'Choose a hospital' : 'No hospitals on the server'}
                </option>
                {step.hospitals.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={common.fieldLabel} htmlFor="setup-robot">
                ROBOT
              </label>
              <select
                id="setup-robot"
                className={styles.input}
                value={robotId}
                disabled={!hospital || hospital.robots.length === 0}
                onChange={(e) => setRobotId(e.target.value)}
              >
                <option value="" disabled>
                  {!hospital
                    ? 'Choose the hospital first'
                    : hospital.robots.length
                      ? 'Choose the robot'
                      : 'Every robot here already has a tablet'}
                </option>
                {hospital?.robots.map((r) => (
                  <option key={r.id} value={r.id}>
                    {robotLabel(r)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.actions}>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  setStep({ kind: 'code' });
                  setError(null);
                }}
              >
                BACK
              </Button>
              <Button type="submit" variant="primary" size="lg" disabled={busy || !robotId}>
                {busy ? 'SYNCING…' : 'SET UP AND SYNC'}
              </Button>
            </div>
          </form>
        )}

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Frees this tablet's robot, so the tablet can be set up for another one (or another tablet for
 * this robot). Never while records wait for the server or a cycle runs.
 */
export function ResetTabletScreen({ onClose }: { readonly onClose: () => void }) {
  const setup = useTabletSetup();
  const sync = useSyncStatus();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!setup || !sync) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await setup.reset(code);
    setBusy(false);
    setCode('');
    // Reset: the app shows the setup screen by itself.
    if (result.ok) onClose();
    else setError(problemText(result, sync.pending));
  };

  return (
    <section className={cx(common.screen, common.fadeUp, styles.screen)} aria-label="This tablet">
      <div className={cx(common.card, styles.card)}>
        <div className={common.kicker}>TABLET SETUP</div>
        <h2 className={styles.heading}>This Tablet</h2>
        <dl className={styles.rows}>
          <div>
            <dt>ROBOT</dt>
            <dd>{sync.robot?.id ?? '—'}</dd>
          </div>
          <div>
            <dt>HOSPITAL</dt>
            <dd>{sync.robot?.hospitalName ?? '—'}</dd>
          </div>
          <div>
            <dt>WAITING FOR THE SERVER</dt>
            <dd>{sync.pending}</dd>
          </div>
        </dl>
        <form onSubmit={(e) => void submit(e)}>
          <p className={styles.note}>
            Resetting frees this robot, so this tablet can be set up for another one. It needs
            mobile data, and every record must have reached the server first.
          </p>
          <CodeField value={code} onChange={setCode} />
          <div className={styles.actions}>
            <Button variant="ghost" size="lg" onClick={onClose}>
              BACK
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="lg"
              disabled={busy || code.length !== PIN_LENGTH}
            >
              {busy ? 'RESETTING…' : 'RESET TABLET'}
            </Button>
          </div>
        </form>
        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
      </div>
    </section>
  );
}
